import { v4 as uuidv4 } from 'uuid';
import findResult, {
  assignIfExists,
  findSelectedInCategory,
  assignValue,
  assignProps,
  findWithReadStatus,
  removeItemByCode,
  mapResults,
  findWithCodes,
  hasMinimumLength,
  mapItemOrderByValues,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { formatDate } from '@/utils/dateHelper';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { pruneFormFieldErrors } from '@/forms/state/state';
import updateMultiplePanels from '@/utils/panels';
import { selectAttachment } from '../attachments/state/state';
import actions, { requestCategories, requestLists, requestTransferTypes } from '../constants';
import initialState, {
  requestsResult,
  pruneRequestErrors,
  pruneFieldErrors,
  selectRequestSources,
  statusPanels,
  newRequest,
  openRequest,
  requestStateToLoad,
  requestWorkspaceAttributes,
  findRequests,
  findRequest,
  findAllRequests,
} from './state';
import crushRequestFields from '../forms/state/state';

const fetchRequests = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const updateRequestsFilter = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.filter = action.value;
});

const updateRequestsFlags = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.pendingFlag = action.pendingFlag;
  item.approvedFlag = action.approvedFlag;
  item.deniedFlag = action.deniedFlag;
  item.errorFlag = action.errorFlag;
  item.currentPage = 1;
});

const showRequestsResults = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, requestsResult) : mapResults([], action, requestsResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showRequestsError = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const toggleRequestsSelected = withDraft((draft, action) => {
  const item = findRequests(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const closeRequests = withDraft((draft, action) => {
  draft.requests[action.category] = requestStateToLoad({ requests: draft }).requests[action.category];
});

const showDeleteRequests = withDraft((draft, action) => {
  draft.deleteRequestOpen = true;
  draft.requests.deletion.results = findSelectedInCategory(draft.requests, action.category);
});

const showArchiveRequests = withDraft((draft, action) => {
  draft.archiveRequestOpen = true;
  draft.requests.deletion.results = findSelectedInCategory(draft.requests, action.category);
});

const fetchRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.requests[action.category].results.push(openRequest(action));
  }
});

const showCloneRequest = withDraft((draft, { category, code, result }) => {
  const item = findRequest(draft, { category, code });
  assignIfExists(item, {
    ...requestWorkspaceAttributes(result),
    destination_namespace: result.destination_namespace,
    dictionaries: result.dictionaries,
    fields: result.fields,
    transfer: { transfer_type: result.transfer.transfer_type },
    readStatus: states.SUCCESS,
    readErrors: [],
  });

  item.steps.namespaceSelected = hasMinimumLength(result.destination_namespace);

  if (result.attachments?.length > 0) {
    const { attachments } = item.steps;
    attachments.selectedInitially = result.attachments
      ?.reduce((acc, cur) => ({ ...acc, [cur.id]: true }), {});

    if (attachments.results) {
      attachments.results = selectAttachment(attachments);
    }
  }
});

const showRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  assignProps(item, {
    ...action.result,
    readStatus: states.SUCCESS,
    readErrors: [],
    ...statusPanels(action.result),
  });
});

const showRequestError = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const showRequestWorkflowStatus = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  assignProps(item, {
    ...action.result,
    ...statusPanels(action.result),
  });
});

const showRequestSourceError = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  const [source] = item.steps.results;
  source.readStatus = states.ERROR;
  source.readErrors = [responseError(action)];
});

const toggleRequestSelected = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  if (!item.selected && !draft.requests[action.category].multiSelect) {
    const selectedRequests = findSelectedInCategory(draft.requests, action.category);
    selectedRequests.forEach((request) => assignValue(request, 'selected', false));
  }
  item.selected = !item.selected;
});

const closeRequest = withDraft((draft, { category, code }) => {
  const item = findRequest(draft, { category, code });
  if (item) {
    draft.requests[category].results = removeItemByCode(draft.requests[category].results, item);
  }
});

const showTransferRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  draft.transferRequestsOpen = true;
  draft.requests.transfer.results = [{ ...item, readStatus: states.PENDING, readErrors: [] }];
});

const showApproveRequest = withDraft((draft, { category, code, action }) => {
  const item = findRequest(draft, { category, code });
  draft.approveRequestsOpen = true;
  draft.requests.approval.action = action;
  draft.requests.approval.results = [item];
});

const showDeleteRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  draft.deleteRequestOpen = true;
  draft.requests.deletion.results = [item];
});

const showArchiveRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  draft.archiveRequestOpen = true;
  draft.requests.deletion.results = [item];
});

const updateRequestPanels = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.panels[action.panel] = updateMultiplePanels(item, action);
});

const newRequestFromSource = withDraft((draft, action) => {
  draft.newRequestCode = uuidv4();
  draft.requests[action.category].results.push(newRequest(action));
});

const updateStep = withDraft((draft, { category, code, step }) => {
  const item = findRequest(draft, { category, code });
  item.isDirty = true;
  if (step < item.steps.activeStepIndex) {
    item.steps.terms = false;
  }
  item.steps.activeStepIndex = step;
});

const createRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.saveStatus = states.PENDING;
  item.saveErrors = [];
});

const newRequestSuccess = withDraft((draft, { category, code, result }) => {
  const item = findRequest(draft, { category, code });
  item.fields = {};
  item.previousCode = item.code;
  assignProps(item, result);
  item.readStatus = states.SUCCESS;
  item.saveStatus = states.SUCCESS;
  item.saveErrors = [];
  item.isDirty = false;
  item.steps.activeStepIndex += 1;
  findWithReadStatus(draft.requests, requestLists, states.SUCCESS).forEach((c) => {
    draft.requests[c].readStatus = states.PENDING;
    draft.requests[c].readErrors = [];
  });
});

const newRequestError = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.saveStatus = states.ERROR;
  item.saveErrors = [responseError(action)];
  item.steps.activeStepIndex += 1;
});

const updateRequestFields = withDraft((draft, {
  category, code, entity, properties,
}) => {
  const item = findRequest(draft, { category, code });
  assignProps(item.fields, properties);
  item.isDirty = true;
  item.saveErrors = pruneFieldErrors(item.saveErrors, { entity, properties });
});

const updateRequestSource = withDraft((draft, { category, code, source }) => {
  const item = findRequest(draft, { category, code });
  item.request_source = source;
  item.steps.results = [];
});

const updateRequestProperties = withDraft((draft, { category, code, properties }) => {
  const item = findRequest(draft, { category, code });
  assignIfExists(item, properties);
  item.saveErrors = pruneRequestErrors(item.saveErrors, { properties });
  item.isDirty = true;
});

const updateRequestTransferType = withDraft((draft, { category, code, transferType }) => {
  const item = findRequest(draft, { category, code });
  assignIfExists(item, { transfer: { transfer_type: transferType } });
  item.isDirty = true;
  if (transferType === requestTransferTypes.MANUAL) {
    item.steps.attachments.results?.forEach(
      (attachment) => assignValue(attachment, 'selected', false),
    );
  }
});

const pollRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.steps.pollStatus = states.PENDING;
});

const markPollingComplete = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.steps.pollStatus = states.SUCCESS;
});

const addSourcesToRequest = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  const {
    sources, workspaces, fields, attachments, locations, namespace,
  } = selectRequestSources(item);
  item.destination_namespace = namespace;
  item.steps.namespaceSelected = item.steps.namespaceSelected || hasMinimumLength(namespace);
  item.steps.attachments = attachments;
  item.steps.results = sources;
  item.steps.workspaces = workspaces;
  item.steps.locations = locations;
  item.fields = fields;
  item.steps.activeStepIndex += 1;
  item.isDirty = true;
});

const removeRequestSteps = withDraft((draft, action) => {
  const item = findRequest(draft, action);
  item.isDirty = false;
  item.isNew = false;
  item.steps = {};
});

const transferRequest = withDraft((draft, { items }) => {
  items.forEach(({ code }) => {
    const item = findResult(draft.requests, 'transfer', code);
    if (item) {
      item.saveStatus = states.PENDING;
      item.saveErrors = [];
    }
  });
});

const approveOrDenyRequest = withDraft((draft, { items }) => {
  items.forEach(({ code }) => {
    const item = findResult(draft.requests, 'approval', code);
    if (item) {
      item.saveStatus = states.PENDING;
      item.saveErrors = [];
    }
  });
});

const approveOrDenyRequestSuccess = withDraft((draft, { items }) => {
  const categories = findAllRequests(draft, { items });
  draft.approveRequestsOpen = false;

  items.forEach((item) => {
    draft.requests.approval.results = removeItemByCode(draft.requests.approval.results, item);
    categories.forEach((category) => {
      const request = findResult(draft.requests, category, item.code);
      if (request) {
        assignIfExists(request, {
          ...item,
          ...{
            saveStatus: states.SUCCESS,
            saveErrors: [],
          },
        });
      }
    });
  });
});

const transferRequestSuccess = withDraft((draft, { items }) => {
  const transferCodes = items.map((transfer) => transfer.request.code);
  const transferCategories = findWithCodes(draft.requests, requestCategories, transferCodes);
  draft.transferRequestsOpen = false;

  items.forEach((item) => {
    draft.requests.transfer.results = removeItemByCode(draft.requests.transfer.results, item.request);
    transferCategories.forEach((category) => {
      const request = findResult(draft.requests, category, item.request.code);
      if (request) {
        assignIfExists(request, {
          saveStatus: states.SUCCESS,
          saveErrors: [],
          transfer: {
            ready_for_transfer: false,
            transferred_at: item.transferred_at,
            transfer_dates: item.transfer_dates,
          },
          transferredOn: item.transferred_at ? formatDate(item.transferred_at) : '',
        });
      }
    });
  });
});

const approveOrDenyRequestError = withDraft((draft) => {
  draft.approveRequestsOpen = false;
  draft.requests.approval = initialState.requests.approval;
});

const transferRequestError = withDraft((draft) => {
  draft.transferRequestsOpen = false;
  draft.requests.transfer = initialState.requests.transfer;
});

const archiveOrDeleteRequests = withDraft((draft, { items }) => {
  findAllRequests(draft, { items }).forEach((category) => {
    items.forEach(({ code }) => {
      const item = findResult(draft.requests, category, code);
      if (item) {
        item.destroyStatus = states.PENDING;
        item.destroyErrors = [];
      }
    });
  });
});

const destroyRequests = withDraft((draft, { items }) => {
  draft.deleteRequestOpen = false;
  draft.archiveRequestOpen = false;

  findAllRequests(draft, { items }).forEach((category) => {
    items.forEach(({ code }) => {
      const item = findResult(draft.requests, category, code);
      if (item) {
        draft.requests[category].results = removeItemByCode(draft.requests[category].results, item);
      }
    });
  });
});

const destroyRequestsError = withDraft((draft, { items }) => {
  draft.deleteRequestOpen = false;
  draft.archiveRequestOpen = false;

  findAllRequests(draft, { items }).forEach((category) => {
    items.forEach(({ code, error }) => {
      const item = findResult(draft.requests, category, code);
      if (item) {
        item.destroyStatus = states.ERROR;
        item.destroyErrors = [error];
      }
    });
  });
});

const hideApproveRequest = withDraft((draft) => {
  draft.approveRequestsOpen = false;
  draft.requests.approval.results = [];
});

const hideTransferRequest = withDraft((draft) => {
  draft.transferRequestsOpen = false;
  draft.requests.transfer.results = [];
});

const hideArchiveOrDeleteRequest = withDraft((draft) => {
  draft.deleteRequestOpen = false;
  draft.archiveRequestOpen = false;
  draft.requests.deletion.results = [];
});

const allowPmnTransfers = withDraft((draft, { value }) => { draft.allowPmnTransfer = value; });

const toggleRequestsPresentation = withDraft((draft, { category, display }) => {
  const item = findRequests(draft, { category });
  item.display = display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const updateRequestFieldsProperties = withDraft((draft, {
  category,
  code,
  properties,
  fields,
  entity,
}) => {
  const item = findRequest(draft, { category, code });

  assignProps(item, { fields: properties });
  item.isDirty = true;
  item.saveErrors = pruneFormFieldErrors(item.saveErrors, { fields, entity });
});

const showWorkflowForRequest = withDraft((draft, { category, requestCode, result }) => {
  const item = findRequest(draft, { category, code: requestCode });

  item.readStatus = states.SUCCESS;
  if (item.isNew) {
    crushRequestFields(item, result);
  }
});

const toggleRequestColumnOrderBy = withDraft((draft, { category, orderByColumn }) => {
  const item = findRequests(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.orderBy = mapItemOrderByValues(item.orderBy, orderByColumn, item.singleSorting);
});

const toggleRequestSingleSorting = withDraft((draft, { category }) => {
  const item = findRequests(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.singleSorting = !item.singleSorting;
  item.orderBy = [item.orderBy[0]];
});

export default {
  [actions.ADD_SOURCES_TO_REQUEST]: addSourcesToRequest,
  [actions.ALLOW_PMN_TRANSFERS]: allowPmnTransfers,
  [actions.APPROVE_REQUEST]: approveOrDenyRequest,
  [actions.APPROVE_REQUEST_ERROR]: approveOrDenyRequestError,
  [actions.APPROVE_REQUEST_SUCCESS]: approveOrDenyRequestSuccess,
  [actions.ARCHIVE_REQUESTS]: archiveOrDeleteRequests,
  [actions.CLOSE_REQUEST]: closeRequest,
  [actions.CLOSE_REQUESTS]: closeRequests,
  [actions.CREATE_REQUEST]: createRequest,
  [actions.DELETE_REQUESTS]: archiveOrDeleteRequests,
  [actions.DENY_REQUEST]: approveOrDenyRequest,
  [actions.DENY_REQUEST_ERROR]: approveOrDenyRequestError,
  [actions.DENY_REQUEST_SUCCESS]: approveOrDenyRequestSuccess,
  [actions.DESTROY_REQUESTS]: destroyRequests,
  [actions.DESTROY_REQUESTS_ERROR]: destroyRequestsError,
  [actions.FETCH_REQUEST]: fetchRequest,
  [actions.FETCH_REQUESTS]: fetchRequests,
  [actions.HIDE_APPROVE_REQUEST]: hideApproveRequest,
  [actions.HIDE_ARCHIVE_REQUEST]: hideArchiveOrDeleteRequest,
  [actions.HIDE_DELETE_REQUEST]: hideArchiveOrDeleteRequest,
  [actions.HIDE_TRANSFER_REQUEST]: hideTransferRequest,
  [actions.MARK_POLLING_COMPLETE]: markPollingComplete,
  [actions.NEW_REQUEST_ERROR]: newRequestError,
  [actions.NEW_REQUEST_FROM_SOURCE]: newRequestFromSource,
  [actions.NEW_REQUEST_SUCCESS]: newRequestSuccess,
  [actions.POLL_REQUEST]: pollRequest,
  [actions.REFRESH_REQUESTS]: fetchRequests,
  [actions.REMOVE_REQUEST_STEPS]: removeRequestSteps,
  [actions.SHOW_APPROVE_REQUEST]: showApproveRequest,
  [actions.SHOW_ARCHIVE_REQUEST]: showArchiveRequest,
  [actions.SHOW_ARCHIVE_REQUESTS]: showArchiveRequests,
  [actions.SHOW_CLONE_REQUEST]: showCloneRequest,
  [actions.SHOW_DELETE_REQUEST]: showDeleteRequest,
  [actions.SHOW_DELETE_REQUESTS]: showDeleteRequests,
  [actions.SHOW_REQUEST]: showRequest,
  [actions.SHOW_REQUEST_ERROR]: showRequestError,
  [actions.SHOW_REQUEST_SOURCE_ERROR]: showRequestSourceError,
  [actions.SHOW_REQUEST_WORKFLOW_STATUS]: showRequestWorkflowStatus,
  [actions.SHOW_REQUEST_WORKFLOW_STATUS_ERROR]: showRequestError,
  [actions.SHOW_REQUESTS_ERROR]: showRequestsError,
  [actions.SHOW_REQUESTS_RESULTS]: showRequestsResults,
  [actions.SHOW_TRANSFER_REQUEST]: showTransferRequest,
  [actions.SHOW_WORKFLOW_FOR_REQUEST]: showWorkflowForRequest,
  [actions.TOGGLE_REQUEST_COLUMN_ORDER_BY]: toggleRequestColumnOrderBy,
  [actions.TOGGLE_REQUEST_SELECTED]: toggleRequestSelected,
  [actions.TOGGLE_REQUEST_SINGLE_SORTING]: toggleRequestSingleSorting,
  [actions.TOGGLE_REQUESTS_PRESENTATION]: toggleRequestsPresentation,
  [actions.TOGGLE_REQUESTS_SELECTED]: toggleRequestsSelected,
  [actions.TRANSFER_REQUEST]: transferRequest,
  [actions.TRANSFER_REQUEST_ERROR]: transferRequestError,
  [actions.TRANSFER_REQUEST_SUCCESS]: transferRequestSuccess,
  [actions.UPDATE_REQUEST_FIELDS]: updateRequestFields,
  [actions.UPDATE_REQUEST_FIELDS_PROPERTIES]: updateRequestFieldsProperties,
  [actions.UPDATE_REQUEST_PANELS]: updateRequestPanels,
  [actions.UPDATE_REQUEST_PROPERTIES]: updateRequestProperties,
  [actions.UPDATE_REQUEST_SOURCE]: updateRequestSource,
  [actions.UPDATE_REQUEST_TRANSFER_TYPE]: updateRequestTransferType,
  [actions.UPDATE_REQUESTS_FILTER]: updateRequestsFilter,
  [actions.UPDATE_REQUESTS_FLAGS]: updateRequestsFlags,
  [actions.UPDATE_STEP]: updateStep,
};
